import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchTasks } from "../redux/actions/taskActions";
import { fetchFiles } from "../redux/actions/fileActions";
import { fetchRelationships } from "../redux/actions/relationshipActions";
import { useNavigate } from "react-router-dom";
import { useDeduplicatedRelationships } from "../hooks/useDeduplicatedRelationships";
import CustomAutocomplete from "../components/CustomAutocomplete";
import FileAutocomplete from "../components/FileAutocomplete";
import { actionButtonStyles } from "../styles/actionButton";
import { textFieldStyles } from "../styles/textFieldStyles";
import { windowsTableStyles } from "../styles/windowsTableStyles";
import Avatar from "@mui/material/Avatar";

import {
  TableSortLabel,
  Button,
  FormControl,
  Container,
  TextField,
  MenuItem,
  Grid,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Autocomplete,
  Breadcrumbs,
  Typography,
  Link,
  Stack,
  InputAdornment,
  TableContainer,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import BreadcrumbNav from "../components/BreadcrumbNav";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import { useTranslation } from 'react-i18next';



// Breadcrumbs will be set in component with translation
const tableStyles = windowsTableStyles();
const buttonStyles = {
  borderRadius: 1,
  padding: "6px 16px",
  textTransform: "none",
  boxShadow: "none",
  height: "fit-content",
  "&:hover": {
    boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.1)",
  },
};

const tableHeaderStyles = {
  backgroundColor: "#f5f5f5",
  color: "#333",
  fontWeight: "bold",
  textTransform: "uppercase",
  letterSpacing: "0.1em",
  padding: "10px 15px",
};

// Add date formatting function
const formatDate = (dateString) => {
  if (!dateString) return '--';
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear().toString().slice(-2); // Get last 2 digits of year
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${day}-${month}-${year} ${hours}:${minutes}`;
};

const TaskPage = () => {
  const { t } = useTranslation(['pages', 'common', 'buttons', 'forms', 'navigation']);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { tasks, loading, error } = useSelector((state) => state.taskList);
  const { files } = useSelector((state) => state.fileList);
  const { relationships: allRelationships } = useSelector((state) => state.relationshipList);
  const { user } = useSelector((state) => state.userLoaded); // Assuming user info is stored in auth state

  // Deduplicate relationships using shared hook
  const relationships = useDeduplicatedRelationships(allRelationships);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [caseFilter, setCaseFilter] = useState(null);
  const [ownerFilter, setOwnerFilter] = useState(null);
  const [ownerFilterCleared, setOwnerFilterCleared] = useState(false);

  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("");

  const breadcrumbs = [{ label: t('navigation:home'), href: "/" }];

  const statusOptions = [
    { value: "Not started", label: t('pages:tasks.status_options.not_started') },
    { value: "In progress", label: t('pages:tasks.status_options.in_progress') },
    { value: "Completed", label: t('pages:tasks.status_options.completed') },
    { value: "Pending", label: t('pages:tasks.status_options.pending') },
    { value: "Deferred", label: t('pages:tasks.status_options.deferred') }
  ];
  const [delayedCheck, setDelayedCheck] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDelayedCheck(true);
    }, 1000); // Delay in milliseconds

    return () => clearTimeout(timer); // Cleanup the timer
  }, []);
  useEffect(() => {
    // Only fetch if not already loaded to avoid unnecessary API calls
    if (!tasks || tasks.length === 0) {
    dispatch(fetchTasks());
    }
    if (!files || files.length === 0) {
    dispatch(fetchFiles());
    }
    if (!allRelationships || allRelationships.length === 0) {
    dispatch(fetchRelationships());
    }
  }, [dispatch, tasks, files, allRelationships]);

  // Auto-populate filter for all users (including Managers)
  useEffect(() => {
    if (user && relationships?.length > 0 && !ownerFilter && !ownerFilterCleared) {
      // Find the user's relationship in the relationships array
      const userRelationship = relationships?.find(
        (rel) => rel._id === user.relationship && rel.type === "Lawyer"
      );
      if (userRelationship) {
        setOwnerFilter(userRelationship);
      }
    }
  }, [user, relationships, ownerFilter, ownerFilterCleared]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCreateTask = () => {
    navigate("/task/create");
  };

  const truncateText = (text, maxLength) => {
    if (text.length <= maxLength) {
      return text;
    }
    return text.substring(0, maxLength) + "...";
  };

  const userFiles = files?.filter((file) => {
    if (user.role === "Manager") {
      return true; // Managers can see all files
    } else if (user.role === "Responsible Lawyer") {
      return file.officeMembers.some(
        (member) =>
          member.member._id === user.relationship &&
          member.role === "Responsible Lawyer"
      );
    } else if (user.role === "Assistant Lawyer") {
      return file.officeMembers.some(
        (member) =>
          member.member._id === user.relationship &&
          member.role === "Treating Lawyer"
      );
    }
    return false;
  });

  const normalizeString = (str) => str?.replace(/\s+/g, "").toLowerCase();

  const filteredTasks = tasks?.filter(
    (task) => {
      // --- AUTOMATIC FILTER BY OWNER ---
      // If user is not a manager, they can only see their own tasks.
      // UNLESS the owner filter has been explicitly cleared by the user
      if (user && user.role !== "Manager" && !ownerFilterCleared && task.owner?._id !== user.relationship) {
        return false;
      }

      // First check if the task belongs to a case the user has access to
      const hasAccessToCase = userFiles.some((file) => file._id === task.case?._id);
      
      // For managers or when owner filter is cleared, show all tasks regardless of case ownership
      const shouldShowByAccessRules = 
        (user.role === "Manager" || ownerFilterCleared) || hasAccessToCase;
      
      if (!shouldShowByAccessRules) {
        return false;
      }

      return (
        (searchTerm
          ? normalizeString(
            `${task.owner?.firstName || ""} ${task.owner?.middleName || ""} ${task.owner?.lastName || ""
            }`
          ).includes(normalizeString(searchTerm)) ||
          normalizeString(task.type).includes(normalizeString(searchTerm)) ||
          normalizeString(task.case?.name).includes(
            normalizeString(searchTerm)
          ) ||
          normalizeString(task.subject).includes(normalizeString(searchTerm)) ||
          normalizeString(task.case?.fileNo).includes(
            normalizeString(searchTerm)
          ) ||
          normalizeString(task.description).includes(
            normalizeString(searchTerm)
          )
          : true) &&
        (statusFilter
          ? normalizeString(task.status) === normalizeString(statusFilter)
          : true) &&
        (caseFilter ? task.case._id === caseFilter._id : true) &&
        (ownerFilter ? task.owner._id === ownerFilter._id : true)
      );
    }
  ) || [];

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedTasks = filteredTasks?.length ? [...filteredTasks].sort((a, b) => {
    if (orderBy) {
      let aValue = a[orderBy];
      let bValue = b[orderBy];

      if (orderBy === "owner") {
        aValue = a.owner
          ? `${a.owner.firstName} ${a.owner.middleName || ""} ${a.owner.lastName
            }`.trim()
          : "";
        bValue = b.owner
          ? `${b.owner.firstName} ${b.owner.middleName || ""} ${b.owner.lastName
            }`.trim()
          : "";
      } else if (orderBy === "assignee") {
        const aAssignee = a.assignee || a.owner;
        const bAssignee = b.assignee || b.owner;
        aValue = aAssignee
          ? `${aAssignee.firstName} ${aAssignee.middleName || ""} ${aAssignee.lastName || ""}`.trim()
          : "";
        bValue = bAssignee
          ? `${bAssignee.firstName} ${bAssignee.middleName || ""} ${bAssignee.lastName || ""}`.trim()
          : "";
      } else if (orderBy === "case") {
        aValue = a.case ? a.case.name : "";
        bValue = b.case ? b.case.name : "";
      }

      if (order === "asc") {
        return aValue < bValue ? -1 : 1;
      } else {
        return aValue > bValue ? -1 : 1;
      }
    }
    return 0;
  }) : [];

  const stringToColor = (string) => {
    if (!string) return "#ccc"; // Default gray color if string is empty

    let hash = 0;
    for (let i = 0; i < string.length; i++) {
      hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }

    let color = "#";
    for (let i = 0; i < 3; i++) {
      const value = (hash >> (i * 8)) & 0xff;
      color += ("00" + value.toString(16)).slice(-2);
    }

    return color;
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case t('pages:tasks.priority_options.high'):
      case "High":
        return "red"; // Red for high priority
      case t('pages:tasks.priority_options.medium'):
      case "Medium":
        return "orange"; // Orange for medium priority
      case t('pages:tasks.priority_options.low'):
      case "Low":
        return "green"; // Green for low priority
      default:
        return "gray"; // Default color if no priority is set
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case t('pages:tasks.status_options.in_progress'):
      case "In progress":
        return "blue";  // Blue for In Progress
      case t('pages:tasks.status_options.not_started'):
      case "Not started":
        return "gray";  // Gray for Not Started
      case t('pages:tasks.status_options.completed'):
      case "Completed":
        return "#85bc20";  // Green for Completed
      case t('pages:tasks.status_options.pending'):
      case "Pending":
        return "orange";  // Orange for Pending
      case t('pages:tasks.status_options.deferred'):
      case "Deferred":
        return "gray";  // Gray for Deferred
        default:
        return "black";  // Transparent if no status
    }
  };
  const getColor = (color) => {
    switch (color) {
      case "Red":
        return "#e5202e";  // Blue for In Progress
      case "Blue":
        return "Blue";  // Gray for Not Started
      case "Green":
        return "#85bc20";  // Green for Completed
      case "Yellow":
        return "Yellow";
      default:
        return "transparent";  // Transparent if no status
    }
  };



  return (
    <Container
      maxWidth="lg"
      sx={{
        backgroundColor: "white",
        boxShadow: "0px 4px 16px rgba(0,0,0,0.013)", // Reduced shadow spread
        pt: 2,
        marginBottom: 8,
        marginTop: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0",
          margin: "0",
          mb: 4,
          mt: 2,
          gap: 2,
        }}
      >
        <BreadcrumbNav title={t('pages:tasks.title')} breadcrumbs={breadcrumbs} />

        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleCreateTask}
          size="small"
          sx={actionButtonStyles()}
        >
          {t('pages:tasks.create_new')}
        </Button>
      </Box>
      <Box>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                marginBottom: 2,
              }}
            >
              <TextField
                label={t('common:search')}
                variant="outlined"
                value={searchTerm}
                size="small"
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  sx: {
                    // further adjustments to inner padding
                    padding: "0 10px",
                  },
                }}
                sx={textFieldStyles({ width: "25%", marginRight: "0" })}
              // style={{ marginRight: "10px" }}
              />
              <FormControl
                variant="outlined"
                sx={{ width: "25%" }}
                size="small"
              >
                <CustomAutocomplete
                  options={relationships?.filter(
                    (rel) => rel.type === "Lawyer"
                  )}
                  label={t('pages:tasks.filter_by_owner')}
                  value={ownerFilter}
                  onChange={(event, newValue) => {
                    setOwnerFilter(newValue);
                    // Track if filter was manually cleared
                    if (newValue === null) {
                      setOwnerFilterCleared(true);
                    } else {
                      setOwnerFilterCleared(false);
                    }
                  }}
                  filterType="Member"
                  addNewLabel={t('pages:tasks.add_new_identity')}
                  addNewPath="/relationships/create"
                  showIcon={true}
                  icon={FilterListIcon}
                  showAddNewOption={false}
                />
              </FormControl>
              <FileAutocomplete
                options={userFiles}
                value={caseFilter}
                onChange={(event, newValue) => setCaseFilter(newValue)}
                label={t('common:case')}
                name="case"
                size="small"
                icon={<FilterListIcon />}
                width={"25%"}
                sx={{
                  // width: '100%',
                  backgroundColor: "white",
                }}
              />

              <Autocomplete
                options={statusOptions}
                getOptionLabel={(option) => option.label || option}
                sx={{ width: "25%" }}
                size="small"
                value={statusOptions.find(opt => opt.value === statusFilter) || null}
                onChange={(event, newValue) => setStatusFilter(newValue?.value || "")}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('pages:tasks.filter_by_status')}
                    sx={textFieldStyles({ width: "100%" })}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <FilterListIcon />
                        </InputAdornment>
                      ),
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                )}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            padding: "32px 24px",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          <TableContainer>
            {delayedCheck && filteredTasks?.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "32px 24px",
                }}
              >
                No data available
              </Box>
            ) : (
              <Table sx={tableStyles.container} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableStyles.headerCell}>#</TableCell>
                    <TableCell
                      sx={{ ...tableStyles.headerCell, width: "140px" }}
                    >
                      <TableSortLabel
                        active={orderBy === "owner"}
                        direction={orderBy === "owner" ? order : "asc"}
                        onClick={() => handleRequestSort("owner")}
                      >
                        {t('common:owner')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sx={{ ...tableStyles.headerCell, width: "140px" }}
                    >
                      <TableSortLabel
                        active={orderBy === "assignee"}
                        direction={orderBy === "assignee" ? order : "asc"}
                        onClick={() => handleRequestSort("assignee")}
                      >
                        {t('common:assignee')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sx={{ ...tableStyles.headerCell, width: "150px" }}
                    >
                      <TableSortLabel
                        active={orderBy === "type"}
                        direction={orderBy === "type" ? order : "asc"}
                        onClick={() => handleRequestSort("type")}
                      >
                        {t('common:type')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sx={{ ...tableStyles.headerCell, width: "150px" }}
                    >
                      <TableSortLabel
                        active={orderBy === "owner"}
                        direction={orderBy === "owner" ? order : "asc"}
                        onClick={() => handleRequestSort("client")}
                      >
                        {t('common:client')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sx={{ ...tableStyles.headerCell, width: "200px" }}
                    >
                      <TableSortLabel
                        active={orderBy === "case"}
                        direction={orderBy === "case" ? order : "asc"}
                        onClick={() => handleRequestSort("case")}
                      >
                        {t('common:case')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sx={{ ...tableStyles.headerCell, width: "150px" }}
                    >
                      <TableSortLabel
                        active={orderBy === "subject"}
                        direction={orderBy === "subject" ? order : "asc"}
                        onClick={() => handleRequestSort("subject")}
                      >
                        {t('forms:labels.subject')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sx={{ ...tableStyles.headerCell, width: "100px" }}
                    >
                      <TableSortLabel
                        active={orderBy === "dueDate"}
                        direction={orderBy === "dueDate" ? order : "asc"}
                        onClick={() => handleRequestSort("dueDate")}
                      >
                        {t('forms:labels.dueDate')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sx={{ ...tableStyles.headerCell, width: "100px" }}
                    >
                      <TableSortLabel
                        active={orderBy === "status"}
                        direction={orderBy === "status" ? order : "asc"}
                        onClick={() => handleRequestSort("status")}
                      >
                        {t('forms:labels.status')}
                      </TableSortLabel>
                    </TableCell>
                    {/* <TableCell
                      sx={{ ...tableStyles.headerCell, width: "100px" }}
                    >
                      <TableSortLabel
                        active={orderBy === "priority"}
                        direction={orderBy === "priority" ? order : "asc"}
                        onClick={() => handleRequestSort("priority")}
                      >
                        {t('forms:labels.priority')}
                      </TableSortLabel>
                    </TableCell> */}
                    {/* <TableCell sx={{...tableStyles.headerCell, width: "100px"}}>
              <TableSortLabel
                active={orderBy === "colour"}
                direction={orderBy === "colour" ? order : "asc"}
                onClick={() => handleRequestSort("colour")}
              >
                {t('common:colour')}
              </TableSortLabel>
            </TableCell> */}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedTasks
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((task, index) => (
                      <TableRow
                        key={task._id}

                        sx={{ ...tableStyles.row, }}
                      >
                        <TableCell sx={{ ...tableStyles.cell, width: "1rem!important" }}>
                          {page * rowsPerPage + index + 1}
                        </TableCell>

                        <TableCell sx={{ ...tableStyles.cell, }}
                        // onClick={() => { navigate(`/relationships/${task?.owner?._id}`); }}
                        >
                          {task.owner ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Avatar
                                alt={task.owner.firstName}
                                src={task.owner.avatar ? task.owner.avatar : ""}
                                sx={{
                                  fontFamily: '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                                  fontSize: "7.8px",
                                  height: "1rem",
                                  width: "1rem",
                                  marginRight: "-2px",
                                
                                  color: "#fff",
                                }}
                              >
                                {!task.owner.avatar &&
                                  `${task.owner.firstName.charAt(0)}${task.owner.middleName ? task.owner.middleName.charAt(0) : task.owner.lastName.charAt(0)}`}
                              </Avatar>
                              {`${task.owner.firstName} ${task.owner.middleName ? task.owner.middleName + " " : ""}${task.owner.lastName}`}
                            </Box>
                          ) : (
                            "N/A"
                          )}
                        </TableCell>
                        <TableCell sx={tableStyles.cell}>
                          {(() => {
                            const displayPerson = task.assignee || task.owner;
                            return displayPerson ? (
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Avatar
                                  alt={displayPerson.firstName}
                                  src={displayPerson.avatar ? displayPerson.avatar : ""}
                                  sx={{
                                    fontFamily: '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                                    fontSize: "7.8px",
                                    height: "1rem",
                                    width: "1rem",
                                    marginRight: "-2px",
                                    color: "#fff",
                                  }}
                                >
                                  {!displayPerson.avatar &&
                                    `${displayPerson.firstName?.charAt(0) || ""}${displayPerson.middleName ? displayPerson.middleName.charAt(0) : displayPerson.lastName?.charAt(0) || ""}`}
                                </Avatar>
                                {`${displayPerson.firstName || ""} ${displayPerson.middleName ? displayPerson.middleName + " " : ""}${displayPerson.lastName || ""}`.trim() || "N/A"}
                              </Box>
                            ) : (
                              "N/A"
                            );
                          })()}
                        </TableCell>
                        <TableCell
                          sx={{ ...tableStyles.cell, "&:hover": { cursor: "pointer", textDecoration: "underline", }, color: "#005b92", }}
                          onClick={() => navigate(`/task/${task._id}`)}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <FormatListNumberedIcon sx={{ fontSize: 16, color: "gray" }} />
                            <span>{truncateText(
                              (() => {
                                const taskType = [
                                  { value: "Synchronization with Outlook", label: t('pages:tasks.types.synchronization_outlook') },
                                  { value: "Task in the procedure", label: t('pages:tasks.types.task_procedure') },
                                  { value: "Other task", label: t('pages:tasks.types.other_task') },
                                  { value: "Task directed towards the client", label: t('pages:tasks.types.task_client') },
                                  { value: "Phone note (lawyer) opposing party", label: t('pages:tasks.types.phone_note_lawyer_opposing') },
                                  { value: "Phone note client", label: t('pages:tasks.types.phone_note_client') },
                                  { value: "Phone note third party", label: t('pages:tasks.types.phone_note_third_party') },
                                ].find(type => type.value === task.type);
                                return taskType ? taskType.label : task.type;
                              })(), 40)}</span>
                          </Box>
                        </TableCell>

                        <TableCell
                          sx={{
                            ...tableStyles.cell,
                            cursor: "pointer",
                          }}
                        >
                          {task.case?.parties?.find(p => p.type === "Client") ? (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Avatar
                                alt={
                                  task.case.parties.find((party) => party.type === "Client")
                                    ?.name?.businessName || "N/A"
                                }
                                src={
                                  task.case.parties.find((party) => party.type === "Client")
                                    ?.name?.avatar || "/static/images/avatar/1.jpg"
                                }
                                sx={{
                                  fontFamily: '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                                  fontSize: "7.8px",
                                  height: "1rem",
                                  width: "1rem",
                                  marginRight: "-2px",
                                }}
                              />
                              <Box
                                sx={{
                                  
                                  lineHeight: "14px",
                                }}
                              >
                                {(() => {
                                  const client = task.case.parties.find(
                                    (party) => party.type === "Client"
                                  )?.name;
                                  return client
                                    ? client.businessName ||
                                    `${client.firstName || ""} ${client.lastName || ""}`.trim()
                                    : "N/A";
                                })()}
                              </Box>
                            </Box>
                          ) : (
                            "--"
                          )}
                        </TableCell>

                        {/* {console.log("checking", task)} */}
                        <TableCell sx={{ ...tableStyles.cell, "&:hover": { cursor: 'pointer', textDecoration: "underline" }, color: "#005b92", }}
                          onClick={() => navigate(`/files/${task.case._id
                            }/overview`)}
                        >
                          {task.case ? (
                            <>
                              <Stack direction="row">
                                <Typography
                                  variant="body2"
                                  // color="textSecondary"
                                  sx={{ fontSize: "0.7rem", }}
                                >
                                  {task.case.fileNo} {truncateText(task.case.name, 25)}
                                </Typography>

                              </Stack>
                            </>
                          ) : (
                            "N/A"
                          )}
                        </TableCell>
                        <TableCell sx={tableStyles.cell}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box
                              sx={{
                                height: "10px",
                                width: "10px",
                                backgroundColor: getColor(task?.colour),

                              }}
                            />
                            <span>{truncateText(task.subject, 40)}</span>
                          </Box>
                        </TableCell>

                        <TableCell sx={tableStyles.cell}>
                          {formatDate(task.dueDate)}
                        </TableCell>
                        <TableCell sx={{ ...tableStyles.cell }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box
                              sx={{
                                padding: "1px 6px",
                                borderRadius: "20px",
                                backgroundColor: getStatusColor(task.status), // Dynamic color based on status
                                color: (task.status === "Completed" || task.status === t('pages:tasks.status_options.completed')) ? "black" : "white", // White text inside the badge
                                fontSize: "8.25px",
                                fontFamily: '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                                fontWeight: "700",
                                textTransform: "uppercase",
                                letterSpacing: "0.08em",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {(() => {
                                const statusOption = statusOptions.find(option => option.value === task.status);
                                return statusOption ? statusOption.label : task.status;
                              })()}
                            </Box>
                          </Box>
                        </TableCell>


                        {/* <TableCell sx={tableStyles.cell}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box
                              sx={{
                                width: "12px",
                                height: "12px",
                                // borderRadius: "50%", 
                                backgroundColor: getPriorityColor(task.priority),
                              }}
                            />
                            {task.priority}
                          </Box>
                        </TableCell> */}

                        {/* <TableCell sx={tableStyles.cell}>
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      backgroundColor:
                        task.colour === "No colour" || task.colour === t('pages:tasks.colours.no_colour')
                          ? "#FFFFFF"
                          : task.colour.toLowerCase(),
                      border: "1px solid #000",
                    }}
                  />
                </TableCell> */}
                      </TableRow>
                    ))}
                </TableBody>
              </Table>

            )}
            <TablePagination
              component="div"
              count={filteredTasks.length}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              labelRowsPerPage={t('common:rows_per_page')}
            />
          </TableContainer>
        </Box>
      )}
    </Container>
  );
};

export default TaskPage;
